import React, { useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Grid, Button, TextField, Box, Typography } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const INITIAL_VALUE = {
  paymentMethod: "",
  bankName: null,
  bankAccount: "",
  paymentDate: getTodayDate(),
  amount: 0,
  remarks: "",
};

const ShipmentRefundFinanceForm = ({
  onSubmit = () => { },
  isBusy = false,
  refundAmount = 0,
  shipmentRefundDetail = null,
}) => {
  const isCodTransfer = Boolean(shipmentRefundDetail?.isTransferToCOD);
  const exactRefundAmount = Number(
    isCodTransfer
      ? shipmentRefundDetail?.codAmount ?? refundAmount ?? 0
      : Number(refundAmount) > 0
        ? refundAmount
        : shipmentRefundDetail?.amount ?? 0
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { ...INITIAL_VALUE, amount: exactRefundAmount },
    mode: "onChange",
  });

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const { data: paymentMethodList = [] } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const _mappedPaymentMethodList = useMemo(
    () =>
      Array.isArray(paymentMethodList)
        ? paymentMethodList
          .filter((item) => item?.name?.trim().toLowerCase() !== "nchl")
          .map((item) => ({
            label: item?.name || "",
            value: item?._id || "",
          }))
        : [],
    [paymentMethodList]
  );

  const _watchPaymentMethod = useWatch({ control, name: "paymentMethod" });

  const selectedPaymentMethod = useMemo(() => {
    if (!Array.isArray(paymentMethodList) || !_watchPaymentMethod) return null;
    return paymentMethodList.find((item) => item._id === _watchPaymentMethod);
  }, [_watchPaymentMethod, paymentMethodList]);

  const handleFormSubmit = (data) => {
    const methodName = selectedPaymentMethod?.name || "";
    const payload = {
      ...data,
      amount: Number(data.amount),
      refundMethod: methodName || null,
      bankName:
        methodName === "Bank" ? data.bankName : null,
      bankAccount:
        methodName === "Bank" ? data.bankAccount : null,
    };
    onSubmit(payload);
  };

  return (
    <AppForm loading={isBusy || loadingBankNameOptions}>
      <AppForm.Content>
        <Grid container spacing={3}>
          {/* COD Transfer Info Banner */}
          {isCodTransfer && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  border: "1px solid #90caf9",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="primary.main"
                  fontWeight={700}
                  gutterBottom
                >
                  COD Transfer & Charge Price Adjustment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approving this will automatically create a new{" "}
                  <strong>Cash on Delivery</strong> price line for vendor
                  remittance and adjust the existing Charge price.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Original Charge
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      Rs. {shipmentRefundDetail?.originalChargeAmount ?? 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="primary.main">
                      COD Amount to Push
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary.main"
                      fontWeight={700}
                    >
                      Rs.{" "}
                      {shipmentRefundDetail?.codAmount ??
                        refundAmount ??
                        0}
                    </Typography>
                  </Grid>
                  {shipmentRefundDetail?.adjustmentMessage && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Adjustment Message / Note:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: "italic", mt: 0.5 }}
                      >
                        "{shipmentRefundDetail.adjustmentMessage}"
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          )}

          {!isCodTransfer && (
            <Grid item md={6}>
              <Controller
                name="paymentMethod"
                control={control}
                rules={{ required: "Payment Method is required" }}
                render={({ field }) => (
                  <BaseSelect
                    fullWidth
                    required
                    label="Payment Method"
                    options={_mappedPaymentMethodList}
                    getOptionLabel={(opt) => opt.label || ""}
                    getOptionValue={(opt) => opt.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.paymentMethod}
                    helperText={errors.paymentMethod?.message}
                  />
                )}
              />
            </Grid>
          )}

          {!isCodTransfer && selectedPaymentMethod?.name === "Bank" && (
            <Grid item md={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Bank Name is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Bank Name"
                    value={
                      bankNameOptions?.find((opt) => opt._id === field.value) ??
                      null
                    }
                    onChange={(_, data) => field.onChange(data?._id || null)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={bankNameOptions || []}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                  />
                )}
              />
            </Grid>
          )}

          {!isCodTransfer && selectedPaymentMethod?.name === "Bank" && (
            <Grid item md={6}>
              <Controller
                name="bankAccount"
                control={control}
                rules={{ required: "Bank Account is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Bank Account Number"
                    size="small"
                    error={!!errors.bankAccount}
                    helperText={errors.bankAccount?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={isCodTransfer ? 6 : 6}>
            <Controller
              name="paymentDate"
              control={control}
              rules={{
                required: "Date is required",
                validate: (value) => {
                  const selectedDate = new Date(value);
                  const today = new Date();
                  selectedDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  if (selectedDate > today) return "Future dates are not allowed";
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={isCodTransfer ? "Approval Date" : "Payment Date"}
                  required
                  disabled
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.paymentDate}
                  helperText={errors.paymentDate?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={isCodTransfer ? 6 : 6}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: "Refund amount is required",
                validate: (value) => {
                  const amount = Number(value);
                  if (!Number.isFinite(amount) || amount <= 0) {
                    return "Refund amount must be greater than zero";
                  }
                  if (!isCodTransfer && amount > exactRefundAmount) {
                    return `Refund amount cannot exceed Rs. ${exactRefundAmount}`;
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label={isCodTransfer ? "COD Amount to Push" : "Refund Amount"}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0.01, max: !isCodTransfer ? exactRefundAmount : undefined, step: 0.01 }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message || "Editable finance-approved amount"}
                />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Finance Remarks"
                  size="small"
                  multiline
                  rows={2}
                  error={!!errors.remarks}
                  helperText={errors.remarks?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={handleSubmit(handleFormSubmit)} variant="contained">
          {isCodTransfer ? "Approve & Push to COD Price" : "Approve Refund"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentRefundFinanceForm;
